import React from 'react';
import { Box, Divider, Drawer } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// 분리된 컴포넌트들
import { DrawerHeader } from '../Layout';
import { ProjectSection, ProjectCreateModal, ProjectMenuPopover } from '../Project';
import RecentHistorySection from './RecentHistorySection';
import HistoryItemMenu from './HistoryItemMenu';

// 훅과 유틸리티
import { useProjectManagement } from '../../hooks/useHistory/useProjectManagement';
import { useHistoryDrawerState } from '../../hooks/useHistory';
import { useAuth } from '../../hooks/useAuth';

// 스타일
import { drawerStyles, sectionStyles } from '../../styles/drawerStyles';

const HistoryDrawer = ({
  drawerOpen,
  setDrawerOpen,
  history,
  setHistory,
  selectedHistoryId,
  setSelectedHistoryId,
  setAnalyzedVideoId = () => {},
  setAnalyzedTitle = () => {},
  setAnalyzedUrl = () => {},
  editingHistoryId,
  setEditingHistoryId,
  editingTitle,
  setEditingTitle,
  handleEditHistory,
  handleDeleteClick,
  userId
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // 프로젝트 관리 훅
  const projectManagement = useProjectManagement(history, selectedHistoryId, setSelectedHistoryId);

  // Firestore 기반 프로젝트 상태/함수 관리
  const historyDrawerState = useHistoryDrawerState(history, userId, setHistory);

  // 홈으로 이동 핸들러
  const handleGoHome = () => {
    setDrawerOpen(false);
    navigate('/');
  };

  return (
    <Drawer
      anchor="left"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      sx={{
        '& .MuiDrawer-paper': drawerStyles.paper,
      }}
    >
      <DrawerHeader onGoHome={handleGoHome} />

      <Box sx={sectionStyles.container}>
        {/* 프로젝트 섹션 */}
        <ProjectSection
          projects={historyDrawerState.projects}
          history={history}
          openProjects={projectManagement.openProjects}
          toggleProject={projectManagement.toggleProject}
          setProjectModalOpen={historyDrawerState.setProjectModalOpen}
          setProjectMenuAnchor={historyDrawerState.setProjectMenuAnchor}
          setProjectMenuIdx={historyDrawerState.setProjectMenuIdx}
          selectedHistoryId={selectedHistoryId}
          setDrawerOpen={setDrawerOpen}
          setHistoryMenuAnchor={historyDrawerState.setHistoryMenuAnchor}
          setHistoryMenuIdx={historyDrawerState.setHistoryMenuIdx}
          projectSubMenuRefs={historyDrawerState.projectSubMenuRefs}
          historyMenuRefs={historyDrawerState.historyMenuRefs}
          handleAddHistoryToProject={historyDrawerState.handleAddHistoryToProject}
          handleEditHistory={handleEditHistory}
          handleDeleteClick={handleDeleteClick}
          menuOpenIdx={projectManagement.menuOpenIdx}
          setMenuOpenIdx={projectManagement.setMenuOpenIdx}
          setHistory={setHistory}
          setAnalyzedVideoId={setAnalyzedVideoId}
          setAnalyzedTitle={setAnalyzedTitle}
          setAnalyzedUrl={setAnalyzedUrl}
        />

        <Divider sx={sectionStyles.divider} />

        {/* 최근 히스토리 섹션 */}
        <RecentHistorySection
          history={history}
          selectedHistoryId={selectedHistoryId}
          setSelectedHistoryId={setSelectedHistoryId}
          setAnalyzedVideoId={setAnalyzedVideoId}
          setAnalyzedTitle={setAnalyzedTitle}
          setAnalyzedUrl={setAnalyzedUrl}
          setDrawerOpen={setDrawerOpen}
          isDesktop={isDesktop}
          projectSubMenuRefs={historyDrawerState.projectSubMenuRefs}
          historyMenuRefs={historyDrawerState.historyMenuRefs}
          setHistoryMenuAnchor={historyDrawerState.setHistoryMenuAnchor}
          setHistoryMenuIdx={historyDrawerState.setHistoryMenuIdx}
          projects={historyDrawerState.projects}
          handleAddHistoryToProject={historyDrawerState.handleAddHistoryToProject}
          handleEditHistory={handleEditHistory}
          handleDeleteClick={handleDeleteClick}
          menuOpenIdx={projectManagement.menuOpenIdx}
          setMenuOpenIdx={projectManagement.setMenuOpenIdx}
          setHistory={setHistory}
        />

        {/* 프로젝트 삭제 메뉴 */}
        {historyDrawerState.projectMenuAnchor && (
          <ProjectMenuPopover
            projectMenuAnchor={historyDrawerState.projectMenuAnchor}
            setProjectMenuAnchor={historyDrawerState.setProjectMenuAnchor}
            setProjectMenuIdx={historyDrawerState.setProjectMenuIdx}
            handleDeleteProject={() => historyDrawerState.handleDeleteProject(historyDrawerState.projects[historyDrawerState.projectMenuIdx]?.id)}
            updateProjectName={(newName) => historyDrawerState.updateProjectName(historyDrawerState.projects[historyDrawerState.projectMenuIdx]?.id, newName)}
            projectName={historyDrawerState.projects[historyDrawerState.projectMenuIdx]?.name}
          />
        )}

        {/* 프로젝트 생성 모달 */}
        <ProjectCreateModal
          projectModalOpen={historyDrawerState.projectModalOpen}
          setProjectModalOpen={historyDrawerState.setProjectModalOpen}
          newProjectName={historyDrawerState.newProjectName}
          setNewProjectName={historyDrawerState.setNewProjectName}
          handleAddProject={historyDrawerState.handleAddProject}
        />

        {/* 히스토리 아이템 메뉴 */}
        {historyDrawerState.historyMenuAnchor && (
          <HistoryItemMenu
            anchorEl={historyDrawerState.historyMenuAnchor}
            onClose={() => historyDrawerState.setHistoryMenuAnchor(null)}
            handleEditHistory={handleEditHistory}
            handleDeleteClick={handleDeleteClick}
            projects={historyDrawerState.projects}
            handleAddHistoryToProject={historyDrawerState.handleAddHistoryToProject}
            historyId={historyDrawerState.historyMenuAnchor}
            setIsAnyHistoryEditing={projectManagement.setIsAnyHistoryEditing}
          />
        )}
      </Box>
    </Drawer>
  );
};

export default HistoryDrawer; 